// context/AuthContext.tsx
// Authentication context using Cloudflare Access

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { getMe, type UserProfile } from '../lib/api';

interface SignInResult {
  error: { message: string } | null;
}

interface AuthContextType {
  user: UserProfile | null;
  loading: boolean;
  isAuthenticated: boolean;
  isAuthorized: boolean;
  isSuper: boolean;
  assignedBuildings: string[];
  canAccessBuilding: (buildingId: string) => boolean;
  refreshAuth: () => Promise<void>;
  // For demo mode and Cloudflare Access compatibility
  signIn: (email: string, password: string) => Promise<SignInResult>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Demo mode flag - set to false when Cloudflare Access is configured
// When true, bypasses API auth and uses hardcoded demo user
const DEMO_MODE = true;

// Demo user for local development
const DEMO_USER: UserProfile = {
  id: 'user-admin',
  email: 'ben@fpdigital.io',
  full_name: 'Ben Fox',
  role: 'super_admin',
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [assignedBuildings, setAssignedBuildings] = useState<string[]>([]);

  const checkAuth = async () => {
    setLoading(true);

    try {
      // In demo mode, use demo user with access to ALL buildings
      if (DEMO_MODE) {
        setUser(DEMO_USER);
        setIsAuthenticated(true);
        setIsAuthorized(true);
        // Empty array + super_admin role = access to all buildings
        setAssignedBuildings([]);
        setLoading(false);
        return;
      }

      // Check with backend - Cloudflare Access headers are passed automatically
      const { data, error } = await getMe();

      if (error || !data) {
        // Not authenticated or error
        setUser(null);
        setIsAuthenticated(false);
        setIsAuthorized(false);
        setAssignedBuildings([]);
        return;
      }

      setIsAuthenticated(data.authenticated);
      setIsAuthorized(data.authorized);

      if (data.user) {
        setUser(data.user);
        setAssignedBuildings(data.buildings || []);
      } else {
        setUser(null);
        setAssignedBuildings([]);
      }
    } catch (err) {
      console.error('Auth check failed:', err);
      setUser(null);
      setIsAuthenticated(false);
      setIsAuthorized(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const isSuper = user?.role === 'super_admin';

  const canAccessBuilding = (buildingId: string): boolean => {
    if (!user) return false;
    if (isSuper) return true;
    return assignedBuildings.includes(buildingId);
  };

  const refreshAuth = async () => {
    await checkAuth();
  };

  // Demo mode sign in (for local development)
  const signIn = async (email: string, _password: string): Promise<SignInResult> => {
    if (DEMO_MODE) {
      // In demo mode, accept any credentials and use demo user with all-building access
      setUser({ ...DEMO_USER, email });
      setIsAuthenticated(true);
      setIsAuthorized(true);
      setAssignedBuildings([]); // Empty + super_admin = all buildings
      return { error: null };
    }
    // In production, Cloudflare Access handles auth - redirect to Access login
    window.location.href = '/cdn-cgi/access/login';
    return { error: null };
  };

  // Sign out - redirect to Cloudflare Access logout or clear demo session
  const signOut = async () => {
    if (DEMO_MODE) {
      setUser(null);
      setIsAuthenticated(false);
      setIsAuthorized(false);
      setAssignedBuildings([]);
      return;
    }
    // In production, redirect to Cloudflare Access logout
    window.location.href = '/cdn-cgi/access/logout';
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAuthenticated,
        isAuthorized,
        isSuper,
        assignedBuildings,
        canAccessBuilding,
        refreshAuth,
        signIn,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
